import { useState, useEffect, useCallback } from "react";
import { downloadDocumentForFileViewer } from "@/api/documents";
import { isMicrosoftOfficeDocument, isNoRender } from "@/lib/utils";
import { useAppContext } from "@/providers/app-context";

interface UsePdfDocumentOptions {
  documentId: string;
  filename: string;
  onClose?: () => void;
  /**
   * When false, the PDF fetch is skipped entirely (e.g. the document renders
   * in the spreadsheet viewer instead — fetching would needlessly trigger
   * server-side PDF conversion for XLSX files).
   */
  enabled?: boolean;
}

/**
 * Handling document fetching and display in the document viewer (as a PDF).
 * 
 * @param options - Configuration options for the PDF document hook
 * @param options.documentId - The unique identifier of the document to fetch
 * @param options.filename - The filename of the document (used for rendering checks and downloads)
 * @param options.onClose - Optional callback function to execute when closing the document viewer
 * 
 * @returns An object containing:
 * - documentData: The document URL or data for rendering
 * - isLoading: Boolean indicating if the document is currently being fetched
 * - hasError: Boolean indicating if an error occurred during fetching
 * - dontRender: Boolean indicating if the document should not be rendered (based on file type)
 * - handleDownload: Function to trigger document download
 * 
 * The hook handles various document states including malware scanning, infected files,
 * and download functionality. It automatically shows confirmation dialogs for error states
 * and manages the document fetching lifecycle.
 */
export const usePdfDocument = ({
  documentId,
  filename,
  onClose,
  enabled = true,
}: UsePdfDocumentOptions) => {
  const [documentData, setDocumentData] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const { useConfirmation } = useAppContext();
  const confirmation = useConfirmation();

  const dontRender = isNoRender(filename);

  // Stable function to show confirmation dialogs
  const showConfirmationDialog = useCallback(
    (title: string, description: string) => {
      confirmation.open({
        title,
        description,
        confirmText: "OK",
        hideCancel: true,
        callback: onClose,
      });
    },
    [confirmation, onClose],
  );

  // Fetch document data
  useEffect(() => {
    if (!enabled || dontRender || !documentId) return;

    const fetchFile = async () => {
      setIsLoading(true);
      setHasError(false);

      try {
        const response = await downloadDocumentForFileViewer(
          documentId,
          false,
          isMicrosoftOfficeDocument(filename),
        );

        if (!response) {
          console.error("Error fetching document: response is undefined");
          setHasError(true);
          setIsLoading(false);
          showConfirmationDialog(
            "Error retrieving the document",
            "No response received from the server.",
          );
          return;
        }

        if (response.url) {
          setDocumentData(response.url);
          setIsLoading(false);
        } else if (response.status === "in_progress") {
          setHasError(true);
          setIsLoading(false);
          showConfirmationDialog(
            "Scan in progress",
            "We are scanning this file for malware, please try again in a few minutes.",
          );
        } else if (response.status === "infected") {
          setHasError(true);
          setIsLoading(false);
          showConfirmationDialog(
            "Malware detected",
            "This file has been flagged as malware and cannot be viewed or downloaded.",
          );
        } else {
          setHasError(true);
          setIsLoading(false);
          showConfirmationDialog(
            "Error retrieving the document",
            "An error occurred while fetching the file.",
          );
        }
      } catch (error) {
        setHasError(true);
        setIsLoading(false);
        console.error("Error fetching document:", error);
      }
    };

    fetchFile();
  }, [documentId, filename, dontRender, enabled]);

  // Download handler
  const handleDownload = useCallback(async () => {
    try {
      const res = await downloadDocumentForFileViewer(documentId, true, false);
      if (res && res.url) {
        const link = document.createElement("a");
        link.href = res.url;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        console.error("Download failed: Invalid response or no URL provided");
      }
    } catch (error) {
      console.error("Error downloading document:", error);
    }
  }, [documentId, filename]);

  return {
    documentData,
    isLoading,
    hasError,
    dontRender,
    handleDownload,
  };
};
